import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, Edit3, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Instructions() {
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  useEffect(() => {
    const fetchInstructions = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/instructions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContent(response.data.content || '');
      } catch (error) {
        toast.error('Failed to fetch instructions');
      } finally {
        setLoading(false);
      }
    };
    fetchInstructions();
  }, []);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/instructions`, { content: editContent }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(editContent);
      setEditing(false);
      toast.success('Instructions updated successfully');
    } catch (error) {
      toast.error('Failed to update instructions');
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return <p className="text-slate-500">No instructions available yet.</p>;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('# ')) return <h1 key={`line-${i}`} className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-blue-900 mb-4 mt-6" style={{ fontFamily: 'Manrope, sans-serif' }}>{line.slice(2)}</h1>;
      if (line.startsWith('## ')) return <h2 key={`line-${i}`} className="text-xl font-bold text-blue-800 mb-3 mt-5">{line.slice(3)}</h2>;
      if (line.startsWith('### ')) return <h3 key={`line-${i}`} className="text-lg font-bold text-blue-700 mb-2 mt-4">{line.slice(4)}</h3>;
      if (line.startsWith('- ')) return <li key={`line-${i}`} className="ml-6 text-slate-700 mb-1 list-disc">{line.slice(2)}</li>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={`line-${i}`} className="font-bold text-slate-800 mb-2">{line.slice(2, -2)}</p>;
      if (line.trim() === '') return <br key={`br-${i}`} />;
      return <p key={`line-${i}`} className="text-slate-700 mb-2 leading-relaxed">{line}</p>;
    });
  };

  if (loading) return <div className="flex items-center justify-center h-96"><p className="text-slate-500">Loading...</p></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="instructions-title" style={{ fontFamily: 'Manrope, sans-serif' }}>How to Use</h1>
          <p className="text-slate-600">Guide for using the Recruitizr platform</p>
        </div>
        {user?.role === 'admin' && !editing && (
          <Button data-testid="edit-instructions-btn" onClick={() => { setEditContent(content); setEditing(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
            <Edit3 size={16} className="mr-2" />Edit Instructions
          </Button>
        )}
      </div>

      {editing ? (
        <div className="bg-white border border-blue-100 rounded shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-bold text-blue-900 uppercase tracking-wider">Editing (Markdown supported)</p>
            <div className="flex gap-2">
              <Button data-testid="save-instructions-btn" onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold" size="sm">
                <Save size={14} className="mr-1" />Save
              </Button>
              <Button data-testid="cancel-edit-btn" onClick={() => setEditing(false)} variant="outline" className="border-slate-300 rounded" size="sm">
                <X size={14} className="mr-1" />Cancel
              </Button>
            </div>
          </div>
          <textarea
            data-testid="instructions-editor"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full h-[500px] p-4 border border-blue-200 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y"
            placeholder="Write instructions using Markdown format..."
          />
        </div>
      ) : (
        <div className="bg-white border border-blue-100 rounded shadow-sm p-8" data-testid="instructions-content">
          <div className="max-w-3xl">
            {renderMarkdown(content)}
          </div>
        </div>
      )}
    </div>
  );
}
